import { Location } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/services/api.service';
import { environment } from '../../../../environments/environment';
import { AddressModel } from '../../../model/address.model';
import { IntegrationSourceInterface } from '../../../model/eng/integration-source.interface';
import { IntegrationInterface } from '../../../model/integration/integration.interface';
import { OauthKickOffModel } from '../../../model/oauth/oauth-kick-off.model';
import { UserDetailModel } from '../../../model/user-detail.model';
import { AuthService } from '../../../services/auth.service';
import { CaptchaService } from '../../../services/captcha.service';
import { ClientService } from '../../../services/client.service';
import { IntegrationService, OauthConnectionType } from '../../../services/integration.service';
import { SharedService } from '../../shared/shared.service';
import { FullAddressModel } from 'src/app/model/common/full-address.model';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { CountableTimeZonePipe } from 'countable@helpers';

@Component({
  selector: 'app-client-portal',
  templateUrl: './client-portal.component.html',
  styleUrls: ['./client-portal.component.scss'],
  standalone: false
})
export class ClientPortalComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('alertDialog') alertDialog: TemplateRef<any>;
  @ViewChild('disconnectDialog') disconnectDialog: TemplateRef<any>;
  @ViewChild('csvDeleteDialog') deleteCsvFileDialog: TemplateRef<any>;

  userDetail: UserDetailModel;
  clientFirmId: number;
  dataLoaded: boolean = false;
  connectionStatus: boolean = true;
  integrations: IntegrationInterface[] = [];
  integration: IntegrationInterface = null;
  tenantInfo: IntegrationSourceInterface = null;
  selectedOrganization;
  organizationsList;

  public zoom: number;
  disableStep3: any = true;
  useraccountId: any;
  firmDetails: any = {};
  jurisdictionDetails: any = [];
  typeOfEntityDetails: any = [];
  clientDetailsForm: UntypedFormGroup;
  customDateTitle: any = 'Incorporation Date';
  clientInformation: any;
  showStep3: any = false;
  uploadedFile: any = [];
  isUploaded: any = false;
  enableComplete: any = false;
  clientDetailsCopy: any;
  integrationAvailable: boolean = false;
  bucketName: any;
  csvDetailsForDelete: any;
  csvDetails: any;
  countryName: any;
  public autoCompleteSuggestions: WritableSignal<FullAddressModel[]> = signal<FullAddressModel[]>([]);


  constructor(public sharedService: SharedService, public apiService: ApiService, private formbuilder: UntypedFormBuilder,private countableTimeZonePipe: CountableTimeZonePipe,
    private router: Router, private route: ActivatedRoute, private loc: Location, private toastr: ToastrService, public dialog: MatDialog,
    private integrationService: IntegrationService, private authService: AuthService,
    private captchaService: CaptchaService, private clientApi: ClientService) {
  }

  ngOnInit() {
    this.userDetail = this.authService.getUserDetail();
    this.clientFirmId = this.userDetail.clientfirmid;
    if (this.userDetail.s3BucketName) {
      this.bucketName = this.userDetail.s3BucketName;
    }

    this.integrationService.getAccountingIntegrationByClient(this.userDetail.clientfirmid).subscribe(tenant => {
      this.tenantInfo = tenant;
      this.integrationService.getAccountingIntegrationsBasicInfo().subscribe(integrations => {
        // get the tenant detail until the result of getConnection comes back!
        this.integrations = Array.from(integrations.values());
        if (tenant && tenant.integrationId) {
          this.integration = integrations.get(tenant.integrationId);
          // check the latest status of the connection
          this.integrationService.checkConnection(tenant.id, tenant.hash).subscribe(status => { this.connectionStatus = status; });
          if (this.integration.needExtraStepAfterAuth && !tenant.tenant) {
            this.getSageOrganizations();
          } else {
            this.integrationAvailable = true;
          }
          localStorage.setItem('integratedSuccessfully', 'success');
        }

        this.route.queryParamMap.subscribe(params => {
          for (const i of this.integrations) {
            if (params.has(i.uiKey)) {
              if (params.get(i.uiKey) === 'true') {
                this.toastr.success(i.label + ' connected successfully');
                localStorage.setItem('partnerClientAccountIntegration', 'Success');
                this.disableStep3 = false;
                this.showStep3 = true;
                if (!this.integration.needExtraStepAfterAuth) {
                  this.integrationAvailable = true;
                }
              } else {
                this.toastr.error('Could not connect to ' + i.label + ', please contact the support team');
              }
              break;
            }
          }
        });
        this.dataLoaded = true;
      });
    });

    if (this.userDetail && this.userDetail.useracctid) {
      this.useraccountId = this.userDetail.useracctid;
      this.firmDetails = {
        firmEmailAddress: this.userDetail.firmEmailAddress,
        firmName: this.userDetail.firmName,
        firmBusinessPhoneNo: this.userDetail.firmBusinessPhoneNo,
        firmCellPhoneNo: this.userDetail.firmCellPhoneNo,
        partnerName: this.userDetail.partnerName
      };
    }

    this.clientDetailsForm = this.formbuilder.group({
      'legalEntityName': new UntypedFormControl(),
      'contactPersonName': new UntypedFormControl('', [Validators.required]),
      'clientEmail': new UntypedFormControl('', [Validators.required, Validators.pattern(environment.EMAIL_REG_EXP)]),
      'businessPhone': new UntypedFormControl(''),
      'cellPhone': new UntypedFormControl(''),
      'jurisdiction': new UntypedFormControl(''),
      'typeOfEntity': new UntypedFormControl('', [Validators.required]),
      'incorporationDate': new UntypedFormControl(),
      'country': new UntypedFormControl('')
    });
    this.getClientDetails();
    this.getJurisdiction();
    this.getTypeOfEntity();
  }

  ngAfterViewInit(): void {
    this.captchaService.showCaptchaBanner();
  }

  checkDates(value: string) {
    this.clientDetailsForm.controls['incorporationDate'].setValue(value);
  }

  getClientDetails() {
    this.clientApi.getIndividualClient(this.userDetail.clientfirmid).subscribe({
      next: res => {
        if (res) {
          if (res.clientdata) {
            this.clientInformation = res.clientdata;
            if (this.clientInformation.incorporationdate == '-' || this.clientInformation.incorporationdate == 'null' || this.clientInformation.incorporationdate == '') {
              this.clientInformation.incorporationdate = null;
            }
            if (this.clientInformation.businessphonenumber == '-') {
              this.clientInformation.businessphonenumber = '';
            }
            if (this.clientInformation.cellphonenumber == '-') {
              this.clientInformation.cellphonenumber = '';
            }
            if (this.clientInformation.jurisdictionofentity == '-' || this.clientInformation.jurisdictionofentity == 'null') {
              this.clientInformation.jurisdictionofentity = '';
            }
            if (this.clientInformation.incorporationdate !== null) {
              this.clientInformation.incorporationdate = moment(this.clientInformation.incorporationdate).toDate();
            }
            this.clientDetailsForm.setValue({
              'legalEntityName': this.clientInformation.businessname,
              'contactPersonName': this.clientInformation.contactperson,
              'businessPhone': this.clientInformation.businessphonenumber,
              'cellPhone': this.clientInformation.cellphonenumber,
              'clientEmail': this.clientInformation.emailaddress,
              'jurisdiction': this.clientInformation.jurisdictionofentity ? this.clientInformation.jurisdictionofentity : '',
              'typeOfEntity': +this.clientInformation.typeofentityid,
              'incorporationDate': this.clientInformation.incorporationdate ? this.clientInformation.incorporationdate : null,
              'country': this.clientInformation.country
            });
            this.onEntityChange();
            this.clientDetailsCopy = {
              legalEntityName: this.clientInformation.businessname,
              contactPersonName: this.clientInformation.contactperson,
              clientEmail: this.clientInformation.emailaddress,
              typeOfEntity: +this.clientInformation.typeofentityid
            };
          }
        }
      }
    });
  }

  getJurisdiction() {
    this.apiService.getJurisdiction().subscribe(data => {
      this.jurisdictionDetails = data;
    });
  }

  getTypeOfEntity() {
    this.apiService.getTypeOfEntity().subscribe(data => {
      this.typeOfEntityDetails = data;
    });
  }

  onEntityChange() {
    let entity: any = this.clientDetailsForm.get('typeOfEntity').value;
    if (entity == 1) {
      this.customDateTitle = 'Incorporation Date';
    } else {
      this.customDateTitle = 'Formation Date';
    }
  }

  //upload file in S3
  uploadFile(event) {
    if (event.target.files && event.target.files.length > 0) {
      this.uploadedFile = event.target.files[0];
      ;
      this.saveFileInS3(this.uploadedFile);
      this.saveToDb();
    }
  }

  public handlePhone(event, type) {
    const onlyNums = event.target.value.replace(/[^0-9]/g, '');
    if (onlyNums.length < 10) {
      if (type == 'cell') {
        this.clientDetailsForm.controls['cellPhone'].setValue(onlyNums);
        // this.cellPhone.setValue(onlyNums);
      } else if (type == 'business') {
        //this.businessPhone.setValue(onlyNums);
        this.clientDetailsForm.controls['businessPhone'].setValue(onlyNums);
      } else {
        return;
      }
    } else if (onlyNums.length === 10) {
      const number = onlyNums.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
      if (type == 'cell') {
        this.clientDetailsForm.controls['cellPhone'].setValue(number);
        //this.cellPhone.setValue(number);
      } else if (type == 'business') {
        this.clientDetailsForm.controls['businessPhone'].setValue(number);
        //this.businessPhone.setValue(number);
      } else {
        return;
      }
    }
  };

  public onlyChars(event, type) {
    const noSpecialCHars = event.target.value.replace(/[^a-zA-Z ]/g, '');
    if (type == 'bn') {
      this.clientDetailsForm.controls['legalEntityName'].setValue(noSpecialCHars);
    } else if (type == 'cp') {
      this.clientDetailsForm.controls['contactPersonName'].setValue(noSpecialCHars);
    } else {
      return;
    }
  }

  public onlyLowercase(event) {
    const lowerCase = event.target.value.toLowerCase();
    this.clientDetailsForm.controls['clientEmail'].setValue(lowerCase);
  }

  saveClientDetails(action) {
    if (this.clientDetailsForm.valid) {
      let clientDetails = this.clientDetailsForm.value;
      if (clientDetails.incorporationDate) {
        clientDetails.incorporationDate = this.countableTimeZonePipe.transform(clientDetails.incorporationDate, 'YYYY-MM-DD');
      }
      let data: any = {
        'pclientfirmid': this.clientInformation ? this.clientInformation.clientfirmid : 0,
        'pbsname': clientDetails.legalEntityName,
        'pcontactperson': clientDetails.contactPersonName,
        'pbphonenumber': clientDetails.businessPhone,
        'pcphonenumber': clientDetails.cellPhone,
        'pemailadd': clientDetails.clientEmail,
        'jusrisdictionOfEntity': clientDetails.jurisdiction,
        'ptypeofentityid': clientDetails.typeOfEntity,
        'pincorporationdate': clientDetails.incorporationDate,
        'ploginid': this.useraccountId,
        'practiceUnitId': 0,
        'firstName': null,
        'lastName': null,
        'address': this.clientInformation.address ? this.clientInformation.address : '',
        'city': this.clientInformation.city ? this.clientInformation.city : '',
        'country': this.clientInformation.country,
        'provinceName': this.clientInformation.provincesname,
        'postalCode': this.clientInformation.postcode ? this.clientInformation.postcode : ''
      };
      this.clientApi.updateClient(data).subscribe(response => {
        if (action != 'complete') {
          this.toastr.success('Client details updated successfully');
        }
        this.clientDetailsCopy = {
          legalEntityName: clientDetails.legalEntityName,
          contactPersonName: clientDetails.contactPersonName,
          clientEmail: clientDetails.clientEmail,
          typeOfEntity: +clientDetails.typeOfEntity
        };
        if (action == 'complete') {
          this.completeOnboarding();
        }
      }, error => {
        this.toastr.error('Error in updating details');
        if (action == 'complete') {
          this.completeOnboarding();
        }
      });
      this.disableStep3 = false;
      this.showStep3 = true;
    }
  }

  validateData() {
    if (this.clientDetailsForm.valid) {
      let clientFormDetails = this.clientDetailsForm.value;
      if (clientFormDetails.incorporationDate) {
        clientFormDetails.incorporationDate = this.countableTimeZonePipe.transform(clientFormDetails.incorporationDate, 'YYYY-MM-DD');
      }
      let clientDetailsObj = {
        legalEntityName: clientFormDetails.legalEntityName,
        contactPersonName: clientFormDetails.contactPersonName,
        // businessPhone: clientFormDetails.businessPhone,
        // cellPhone: clientFormDetails.cellPhone,
        clientEmail: clientFormDetails.clientEmail,
        // jurisdiction: clientFormDetails.jurisdiction,
        typeOfEntity: clientFormDetails.typeOfEntity
        // incorporationDate: clientFormDetails.incorporationDate
      };
      if (JSON.stringify(this.clientDetailsCopy) === JSON.stringify(clientDetailsObj)) {
        //no pending changes in step 2
        this.completeOnboarding();
      } else {
        //there are pending changes in step 2
        const dialogRef = this.dialog.open(this.alertDialog, { disableClose: true });
        dialogRef.afterClosed().subscribe(result => {
        });
      }
    } else {
      this.toastr.error('Please complete required fields in Step 2');
    }
  }

  saveAndCompleteOnboarding() {
    this.saveClientDetails('complete');
  }

  completeOnboarding() {
    this.clientApi.updateProcessStatus(0).subscribe({
      next: response => {
        if (response.status === 200) {
          this.toastr.success('Onboarding completed');
          this.router.navigate(['/dashboard/clients/engagements']);
        }
      }, error: e => {
        this.toastr.error('Error in completing the process');
      }
    });
    //session storage was cleared after connecting quickBook
    sessionStorage.setItem('userDetails', JSON.stringify(this.userDetail));
  }

  saveFileInS3(file) {
    let formData: FormData = new FormData();
    let s3signatureKey = 'Engagements/' + this.userDetail.firmUserAcctId + '_Firm/' + this.userDetail.clientfirmid + '_Client/' + 'TrailBalance';
    let status: any = 0;
    formData.append('status', status);
    formData.append('signatureKey', s3signatureKey);
    formData.append('file', file, file.name);

    this.apiService.saveFinancialDocumentJSON(formData).subscribe(response => {

    });
  }

  saveToDb() {
    let JsonFilePath = 'https://s3.amazonaws.com/' + this.bucketName + '/' + 'Engagements/' + this.userDetail.firmUserAcctId + '_Firm/' + 'Templates/' + 'Templates_JSON/' + this.uploadedFile.name;
    var csvData = {
      'csvdata': {
        'clientfirmid': this.userDetail.clientfirmid,
        'JsonFileName': this.uploadedFile.name,
        'JsonFilePath': JsonFilePath,
        'isdelete': 0,
        'clientcsvdataid': 0,
        'loginid': this.userDetail.useracctid
      }
    };

    let dataJsonString = JSON.stringify(csvData);
    this.clientApi.saveClientCsvData(dataJsonString).subscribe({
      next: res => {

      }, error: e => {
        console.log('error response');
      }
    })
  }

  //delete CSV file model popup
  deleteParticularFileModel(file) {
    this.csvDetailsForDelete = file;
    const dialogRef = this.dialog.open(this.deleteCsvFileDialog, { disableClose: true });
    dialogRef.afterClosed().subscribe(result => {
    });
  }

  oauthKickOff(i: IntegrationInterface) {
    if (!this.clientFirmId) {
      this.toastr.error('Something went wrong, please refresh the page');
      return;
    }

    this.captchaService.getCaptchaToken('accounting').then((captcha) => {
      const data: OauthKickOffModel = {
        client: this.clientFirmId,
        redirectMode: OauthConnectionType.CLIENT_ACCOUNT_SETUP, // enum in IntegrationOauthStateEntity
        key: i.uiKey,
        captcha: captcha
      };
      this.integrationService.oauthProvidersKickOff(data).subscribe({
        next: res => {
          if (res.status === 200) {
            this.toastr.success('You will be redirected for authentication in a moment');
            window.open(res.data, '_self');
          } else {
            res.errors.forEach(error => {
              this.toastr.error(error);
            });
          }
        },
        error: () => {
          this.toastr.error('Could not initiate the oauth procedure, please contact support');
        }
      });
    });
  }

  getSageOrganizations() {
    this.integrationService.getSageOrganizations(this.clientFirmId).subscribe(res => {
      this.organizationsList = res;
    });
  }

  saveOrganization() {
    const orgDetails = {
      'realm': this.selectedOrganization.realm,
      'source': this.selectedOrganization.source,
      'name': this.selectedOrganization.organization
    };
    this.integrationService.saveSageOrganization(orgDetails).subscribe(res => {
      if (res) {
        this.refreshTenant();
        this.integrationAvailable = true;
      } else {
        this.toastr.error('Could not save the Sage organization');
      }
    });
  }

  openDisconnectPopup(type) {
    this.dialog.open(this.disconnectDialog, { disableClose: true });
  }

  disconnect() {
    this.integrationService.disconnect(this.clientFirmId).subscribe(res => {
      localStorage.setItem('integratedSuccessfully', 'success');
      if (res.status === 200) {
        this.integrationAvailable = false;
        localStorage.setItem('partnerClientAccountIntegration', 'Success');
        this.toastr.success('Disconnected from ' + this.integration.label);
      } else {
        this.toastr.error('Facing error disconnecting from ' + this.integration.label + ', please refresh the page');
      }
      this.integrationService.deleteIntegrationSourceByFirm(this.clientFirmId);
      this.tenantInfo = null;
      this.integration = null;
    });
  }

  //Delete CDV file confirmation
  deleteCsvfromDB(file) {
    var deleteCsvfile = {
      'csvdata': {
        'clientfirmid': this.userDetail.clientfirmid,
        'JsonFileName': this.csvDetailsForDelete.JsonFileName,
        'JsonFilePath': this.csvDetailsForDelete.JsonFileName,
        'isdelete': 1,
        'clientcsvdataid': this.csvDetailsForDelete.clientcsvdataid,
        'loginid': this.userDetail.useracctid
      }
    };
    let dataJsonString = JSON.stringify(deleteCsvfile);

    this.clientApi.saveClientCsvData(dataJsonString).subscribe({
      next: res => {
        this.isUploaded = false;
        this.toastr.success(this.csvDetailsForDelete.JsonFileName + ' ' + 'deleted successfully');
      }, error: e => {
        console.log('error response');

      }
    })
  }

  private refreshTenant() {
    this.integrationService.getAccountingIntegrationByClient(this.clientFirmId).subscribe(tenant => {
      this.tenantInfo = tenant;
    });
  }

  ngOnDestroy() {
    this.captchaService.hideCaptchaBanner();
  }

  public onAddressType(address: FullAddressModel[]) {
    this.autoCompleteSuggestions.set(address);
  }

  public selectedSuggestion(matOption: MatAutocompleteSelectedEvent) {
    this.countryName = matOption.option.value.country;
    this.clientDetailsForm.controls['jurisdiction'].setValue(matOption.option.value.jurisdiction);
  }
}
